import { useState, useEffect, useCallback } from 'react';
import {
  Recording,
  getRecordings,
  getRecordingDownloadUrl,
  getGroupsByOrg,
  Group,
} from '../../services/admin';

const PAGE_SIZE = 50;

const statusStyle: Record<string, string> = {
  recording: 'text-blue-300 bg-blue-900/20 border-blue-800',
  completed: 'text-green-300 bg-green-900/20 border-green-800',
  incomplete: 'text-yellow-300 bg-yellow-900/20 border-yellow-800',
  failed: 'text-red-300 bg-red-900/20 border-red-700',
  expired: 'text-gray-400 bg-gray-800/40 border-gray-600',
};

const formatDuration = (ms?: number | null): string => {
  if (!ms) return '—';
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatSize = (bytes?: number | null): string => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Dedicated, recording-focused list - the operational log (LogManager) remains the primary
 * timeline; this page exists purely for browsing/searching audio by recording attributes. */
export default function RecordingsManager({ orgId }: { orgId: string }) {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);

  const [talkGroupId, setTalkGroupId] = useState('');
  const [radioId, setRadioId] = useState('');
  const [status, setStatus] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const groupName = useCallback(
    (id?: string | null) => groups.find((g) => g.id === id)?.name ?? (id ? id.substring(0, 8) : '—'),
    [groups]
  );

  const load = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setError(null);
        const data = await getRecordings(orgId, {
          talk_group_id: talkGroupId || undefined,
          radio_id: radioId || undefined,
          status: status || undefined,
          start: start ? new Date(start).toISOString() : undefined,
          end: end ? new Date(end).toISOString() : undefined,
          limit: PAGE_SIZE,
          offset,
        });
        setRecordings(data.recordings);
        setTotal(data.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load recordings');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [orgId, talkGroupId, radioId, status, start, end, offset]
  );

  useEffect(() => {
    if (!orgId) return;
    getGroupsByOrg(orgId).then(setGroups).catch(() => setGroups([]));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    load();
    const interval = setInterval(() => load(true), 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, talkGroupId, radioId, status, start, end, offset]);

  useEffect(() => {
    setOffset(0);
  }, [talkGroupId, radioId, status, start, end]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">🎙️ Recordings</h2>
        <button onClick={() => load()} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
          🔄 Refresh
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error}</div>}

      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Talk group</label>
          <select value={talkGroupId} onChange={(e) => setTalkGroupId(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm">
            <option value="">All</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Radio</label>
          <input
            value={radioId}
            onChange={(e) => setRadioId(e.target.value)}
            placeholder="Radio ID or name..."
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm">
            <option value="">All</option>
            <option value="recording">Recording</option>
            <option value="completed">Completed</option>
            <option value="incomplete">Incomplete</option>
            <option value="failed">Failed</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">From</label>
          <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">To</label>
          <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-900 text-gray-400 text-sm">
              <tr>
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Talk Group</th>
                <th className="px-4 py-3">Radio ID</th>
                <th className="px-4 py-3">Radio Name</th>
                <th className="px-4 py-3">Duration</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Audio</th>
              </tr>
            </thead>
            <tbody>
              {recordings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">No recordings found</td>
                </tr>
              ) : (
                recordings.map((r) => (
                  <tr key={r.id} className="border-t border-gray-700">
                    <td className="px-4 py-3 text-sm whitespace-nowrap">{new Date(r.started_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm">{groupName(r.talk_group_id)}</td>
                    <td className="px-4 py-3 text-sm">{r.radio_human_id || (r.radio_id ? r.radio_id.substring(0, 8) : '—')}</td>
                    <td className="px-4 py-3 text-sm">{r.radio_name || '—'}</td>
                    <td className="px-4 py-3 text-sm">{formatDuration(r.duration_ms)}</td>
                    <td className="px-4 py-3 text-sm">{formatSize(r.file_size_bytes)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded border ${statusStyle[r.status] ?? ''}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      {r.status === 'completed' ? (
                        <div className="flex items-center gap-2">
                          <audio controls preload="none" className="h-8" style={{ maxWidth: 200 }}>
                            <source src={getRecordingDownloadUrl(r.id)} />
                          </audio>
                          <a href={getRecordingDownloadUrl(r.id)} className="text-xs bg-black/30 hover:bg-black/50 px-2 py-1 rounded whitespace-nowrap">
                            Download
                          </a>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">
                          {r.status === 'expired' ? 'Recording expired' : (r.failure_reason ?? '—')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between items-center text-sm text-gray-400 pt-2 border-t border-gray-700">
        <span>Showing {recordings.length === 0 ? 0 : offset + 1}-{offset + recordings.length} of {total}</span>
        <div className="flex items-center gap-2">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40">← Prev</button>
          <span>Page {currentPage} / {totalPages}</span>
          <button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)} className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40">Next →</button>
        </div>
      </div>
    </div>
  );
}
